import { useEffect, useRef } from 'react'
import { useStableLiveQuery } from '../../../../../lib/db/useStableLiveQuery'
import { useStableEntityArray, baseEntityIdentity } from '../../../../../lib/db/useStableEntityArray'
import { db } from '../../../../../lib/db/indexeddb'
import type { Task, Section } from '../../../../../lib/db/models'

interface UsePageCrossfadeParams {
  projectId: string | undefined
  project: { id: string } | null | undefined
}

interface UsePageCrossfadeResult {
  tasks: Task[] | undefined
  sections: Section[] | undefined
  pendingRestoreRef: React.MutableRefObject<string | null>
  pendingSelectionRestoreRef: React.MutableRefObject<string | null>
  correctDataReady: boolean
}

export function usePageCrossfade({
  projectId,
  project,
}: UsePageCrossfadeParams): UsePageCrossfadeResult {
  // Seed prev with a sentinel so the very first render also counts as a
  // "change" — that arms the restore path on a fresh ProjectPage mount
  // (e.g. after navigating away to Quick Notes and back).
  const prevProjectIdRef = useRef<string | undefined | null>(undefined)
  const pendingRestoreRef = useRef<string | null>(null)
  const pendingSelectionRestoreRef = useRef<string | null>(null)

  // Detect projectId change during render phase (before stale guards + continuous save).
  if (projectId !== prevProjectIdRef.current) {
    pendingRestoreRef.current = projectId ?? null
    pendingSelectionRestoreRef.current = projectId ?? null
    prevProjectIdRef.current = projectId
  }

  // Reactive queries — auto-update on Dexie writes.
  // useLiveQuery keeps stale results from the previous project until the new
  // query resolves. We guard against this by checking projectId on the data.
  const tasksFromDexie = useStableLiveQuery(
    () => projectId ? db.getTasksByProject(projectId) : undefined,
    [projectId]
  )
  const sectionsFromDexie = useStableLiveQuery(
    () => projectId ? db.getSectionsByProject(projectId) : undefined,
    [projectId]
  )

  // Reference stabilization: Dexie creates fresh Task/Section objects on
  // every subscription firing (including the re-subscribe that fires on a
  // tab switch back to ProjectPage). `useStableEntityArray` reuses the
  // previous references when `id|updatedAt` matches, so downstream memos
  // (`segments`, …) bail and the whole ProjectPage subtree (TaskBoard,
  // DndContext, SortableContext, TaskList) memo-holds instead of
  // re-rendering on each switch.
  const tasksRaw = useStableEntityArray(tasksFromDexie, baseEntityIdentity)
  const sectionsRaw = useStableEntityArray(sectionsFromDexie, baseEntityIdentity)

  // Block stale data: if the returned data belongs to a different project,
  // treat it as undefined (renders empty state until correct data arrives).
  const tasks = (!tasksRaw || tasksRaw.length === 0 || tasksRaw[0].projectId === projectId)
    ? tasksRaw : undefined
  const sections = (!sectionsRaw || sectionsRaw.length === 0 || sectionsRaw[0].projectId === projectId)
    ? sectionsRaw : undefined

  // Check if the correct (non-stale) data has arrived.
  const hasCorrectTasks = !tasksRaw || tasksRaw.length === 0 || tasksRaw[0].projectId === projectId
  const hasCorrectSections = !sectionsRaw || sectionsRaw.length === 0 || sectionsRaw[0].projectId === projectId
  const correctDataReady = hasCorrectTasks && hasCorrectSections
    && !!project && project.id === projectId

  // Clear pendingRestoreRef once correct data is available (used by useSelectionCache)
  useEffect(() => {
    if (!correctDataReady) return
    if (pendingRestoreRef.current) {
      pendingRestoreRef.current = null
    }
  }, [correctDataReady])

  return { tasks, sections, pendingRestoreRef, pendingSelectionRestoreRef, correctDataReady }
}
